import { useState } from 'react';
import { useAddOpenItem, useCloseOpenItem, useOpenItems, type OpenItem } from '../../hooks/useTripContinuity';
import { formatDate } from '../../lib/formatters';
import { inputClass } from './TripControls';

const PRIORITY_TONE: Record<OpenItem['priority'], string> = {
  high: 'bg-notification-soft text-notification',
  medium: 'bg-warning-soft text-warning',
  low: 'bg-surface-3 text-text-muted',
};

const btn = 'rounded px-3 py-1.5 min-h-[36px] text-xs font-medium disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';

/**
 * Work left undone at a property. Items stay open across visits until someone
 * resolves them, and the next trip's brief lists them. `tripId` (when shown on
 * a trip) is recorded as where an item was opened or resolved.
 */
export function OpenItemsPanel({ locationId, tripId = null, title = 'Open items at this property' }: {
  locationId: string;
  tripId?: string | null;
  title?: string;
}) {
  const { data, isLoading, isError } = useOpenItems(locationId);
  const [adding, setAdding] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const items = data ?? [];
  const open = items.filter((i) => i.status === 'open');
  const closed = items.filter((i) => i.status !== 'open');

  return (
    <section aria-label={title} className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-text">
          {title}
          {!isLoading && <span className="ml-1.5 text-text-muted font-normal">({open.length})</span>}
        </h3>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className={`${btn} bg-surface-3 hover:bg-surface-strong text-text`}>
            Add item
          </button>
        )}
      </div>

      {adding && <AddItemForm locationId={locationId} tripId={tripId} onDone={() => setAdding(false)} />}

      {isLoading ? (
        <p className="text-xs text-text-muted" role="status">Loading open items…</p>
      ) : isError ? (
        <p className="text-xs text-notification">Open items could not be loaded.</p>
      ) : open.length === 0 ? (
        <p className="text-xs text-text-muted">Nothing open. Anything left undone on a visit belongs here, so the next visit picks it up.</p>
      ) : (
        <ul className="space-y-2">
          {open.map((i) => (
            <OpenItemRow key={i.id} item={i} tripId={tripId} />
          ))}
        </ul>
      )}

      {closed.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowClosed((v) => !v)} className="text-xs text-primary hover:underline min-h-[32px]">
            {showClosed ? 'Hide' : 'Show'} {closed.length} closed item{closed.length === 1 ? '' : 's'}
          </button>
          {showClosed && (
            <ul className="mt-2 space-y-1.5">
              {closed.map((i) => (
                <ClosedItemRow key={i.id} item={i} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function AddItemForm({ locationId, tripId, onDone }: { locationId: string; tripId: string | null; onDone: () => void }) {
  const add = useAddOpenItem();
  const [itemTitle, setItemTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [priority, setPriority] = useState<OpenItem['priority']>('medium');

  return (
    <form
      className="space-y-2 rounded-lg border border-border bg-surface-2 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!itemTitle.trim()) return;
        add.mutate(
          { locationId, title: itemTitle.trim(), detail: detail.trim(), priority, tripId },
          { onSuccess: onDone },
        );
      }}
    >
      <label className="block text-xs text-text-muted space-y-1">
        <span>What is left to do?</span>
        <input value={itemTitle} onChange={(e) => setItemTitle(e.target.value)} placeholder="e.g. Replace UPS battery in IDF-2" className={inputClass} autoFocus />
      </label>
      <label className="block text-xs text-text-muted space-y-1">
        <span>Details (optional)</span>
        <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={2} className={inputClass} />
      </label>
      <fieldset className="flex gap-3 text-sm text-text">
        <legend className="sr-only">Priority</legend>
        {(['high', 'medium', 'low'] as const).map((p) => (
          <label key={p} className="flex items-center gap-1.5 min-h-[32px]">
            <input type="radio" name={`prio-${locationId}`} checked={priority === p} onChange={() => setPriority(p)} />
            {p[0].toUpperCase() + p.slice(1)}
          </label>
        ))}
      </fieldset>
      {add.error && <p role="alert" className="text-xs text-notification">{(add.error as Error).message}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className={`${btn} bg-surface-3 hover:bg-surface-strong text-text`}>Cancel</button>
        <button type="submit" disabled={!itemTitle.trim() || add.isPending} className={`${btn} bg-primary hover:bg-primary-hover text-text-inverse`}>
          {add.isPending ? 'Adding…' : 'Add item'}
        </button>
      </div>
    </form>
  );
}

function OpenItemRow({ item, tripId }: { item: OpenItem; tripId: string | null }) {
  const close = useCloseOpenItem();
  const [closing, setClosing] = useState<null | 'resolved' | 'wont_fix'>(null);
  const [note, setNote] = useState('');

  return (
    <li className="rounded-lg border border-border bg-surface p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-text break-words">{item.title}</p>
          {item.detail && <p className="text-xs text-text-muted whitespace-pre-wrap break-words mt-0.5">{item.detail}</p>}
          <p className="text-[11px] text-text-faint mt-1">
            Opened {formatDate(item.opened_at)}
            {item.opened_by?.full_name ? ` by ${item.opened_by.full_name}` : ''}
            {item.opened_trip ? ` on ${item.opened_trip.project_number}` : ''}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${PRIORITY_TONE[item.priority]}`}>{item.priority}</span>
      </div>

      {closing ? (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!note.trim()) return;
            close.mutate({ item, status: closing, resolution: note.trim(), tripId });
          }}
        >
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={closing === 'resolved' ? 'What was done?' : "Why won't this be done?"}
            aria-label="Closing note"
            className={inputClass}
            autoFocus
          />
          {close.error && <p role="alert" className="text-xs text-notification">{(close.error as Error).message}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setClosing(null)} className={`${btn} bg-surface-3 hover:bg-surface-strong text-text`}>Cancel</button>
            <button type="submit" disabled={!note.trim() || close.isPending} className={`${btn} bg-primary hover:bg-primary-hover text-text-inverse`}>
              {closing === 'resolved' ? 'Mark resolved' : "Close as won't fix"}
            </button>
          </div>
        </form>
      ) : (
        <div className="flex gap-2">
          <button type="button" onClick={() => setClosing('resolved')} className={`${btn} bg-success-soft text-success hover:bg-surface-strong`}>Resolved</button>
          <button type="button" onClick={() => setClosing('wont_fix')} className={`${btn} bg-surface-3 text-text-muted hover:bg-surface-strong`}>Won't fix</button>
        </div>
      )}
    </li>
  );
}

function ClosedItemRow({ item }: { item: OpenItem }) {
  const reopen = useCloseOpenItem();
  return (
    <li className="rounded border border-divider px-3 py-2 text-xs">
      <div className="flex items-start justify-between gap-2">
        <p className="text-text-muted line-through decoration-text-faint break-words">{item.title}</p>
        <button
          type="button"
          disabled={reopen.isPending}
          onClick={() => reopen.mutate({ item, status: 'open', resolution: '', tripId: null })}
          className="shrink-0 text-primary hover:underline"
        >
          Reopen
        </button>
      </div>
      <p className="text-text-faint mt-0.5">
        {item.status === 'resolved' ? 'Resolved' : "Won't fix"} {formatDate(item.resolved_at)}
        {item.resolved_by?.full_name ? ` by ${item.resolved_by.full_name}` : ''}
        {item.resolved_trip ? ` on ${item.resolved_trip.project_number}` : ''}
        {item.resolution ? ` — ${item.resolution}` : ''}
      </p>
    </li>
  );
}
